import * as z from 'zod';

import type {
  AnalyticsManager,
  IdentifyOptions,
  IdentifyOptionsResolver,
} from '@ci/analytics';

import { shutdownIntercom } from './intercom';
import type { IntercomUserJwt } from './server/intercom-user-jwt';

/**
 * Secure Intercom Messenger identity, delivered through Segment (PAY-3983).
 *
 * The Messenger is loaded by Segment's Intercom destination, which reads the
 * identity JWT from the third argument of `analytics.identify`:
 *
 *   analytics.identify(userId, traits, {
 *     integrations: { Intercom: { intercom_user_jwt } },
 *   });
 *
 * Identify is emitted from three places (sign-in, the team workspace provider
 * and personal home), so the token is not added at each of them. It is
 * supplied once, through the analytics manager's identify options resolver,
 * which every identify goes through and which resolves BEFORE any provider
 * sees the call.
 */

/**
 * Refresh this long before expiry, so a token is never sent with seconds left
 * on it and rejected by the time Intercom checks it.
 */
export const INTERCOM_JWT_REFRESH_MARGIN_MS = 5 * 60 * 1000;

export interface IntercomJwtCache {
  /**
   * The token for `userId`, fetched once and reused until it nears expiry.
   * `undefined` when none is available, including a token issued for someone
   * other than `userId`.
   */
  getToken(userId: string): Promise<string | undefined>;
  /** Forget the cached token and ignore any fetch still in flight. */
  clear(): void;
}

export function createIntercomJwtCache(
  fetchJwt: () => Promise<IntercomUserJwt | null>,
  now: () => number = Date.now,
): IntercomJwtCache {
  let cached: IntercomUserJwt | undefined;
  let inFlight: Promise<IntercomUserJwt | null> | undefined;
  // Bumped by clear(), so a fetch that started before a sign-out cannot
  // repopulate the cache for the next user.
  let generation = 0;

  const isFresh = (jwt: IntercomUserJwt) =>
    jwt.expiresAt - now() > INTERCOM_JWT_REFRESH_MARGIN_MS;

  const isUnexpired = (jwt: IntercomUserJwt) => jwt.expiresAt > now();

  // Concurrent identifies (all three sites fire around sign-in) share one
  // request instead of each calling the action.
  const fetchOnce = () => {
    if (!inFlight) {
      const fetchGeneration = generation;

      inFlight = fetchJwt()
        .catch(() => null)
        .then((jwt) => {
          if (fetchGeneration === generation) {
            // A refresh that came back empty (timeout, offline, server error)
            // keeps the token we have. It is still valid for the rest of the
            // refresh margin, and dropping it would send this identify
            // unverified over a transient failure. Sign-out is what forgets a
            // token, through clear().
            if (jwt) {
              cached = jwt;
            }

            inFlight = undefined;
          }

          return jwt;
        });
    }

    return inFlight;
  };

  return {
    async getToken(userId) {
      const usable = cached?.userId === userId ? cached : undefined;

      const jwt =
        usable && isFresh(usable)
          ? usable
          : // Refresh; if that fails, fall back to the current token for as
            // long as it has not actually expired.
            ((await fetchOnce()) ??
            (usable && isUnexpired(usable) ? usable : undefined));

      // The server signs for the SESSION user. If that is not the user being
      // identified, the token would vouch for the wrong person: send none.
      if (!jwt || jwt.userId !== userId) {
        return undefined;
      }

      return jwt.token;
    },

    clear() {
      generation += 1;
      cached = undefined;
      inFlight = undefined;
    },
  };
}

export function toIntercomIdentifyOptions(
  token: string | undefined,
): IdentifyOptions | undefined {
  return token
    ? { integrations: { Intercom: { intercom_user_jwt: token } } }
    : undefined;
}

export function createIntercomIdentifyOptionsResolver(
  cache: IntercomJwtCache,
): IdentifyOptionsResolver {
  return async (userId) =>
    toIntercomIdentifyOptions(await cache.getToken(userId));
}

export const INTERCOM_JWT_ENDPOINT = '/api/intercom/user-jwt';

/**
 * Give up on the token after this long. The identify then goes out without
 * one, and every call queued behind it is released. Analytics must never wait
 * on our server for longer than this.
 */
export const INTERCOM_JWT_FETCH_TIMEOUT_MS = 5_000;

const IntercomUserJwtSchema = z
  .object({
    userId: z.string().min(1),
    token: z.string().min(1),
    expiresAt: z.number(),
  })
  .nullable();

/**
 * Never rejects. Any failure (timeout, offline, a redirect to sign-in, a
 * malformed body) means "no token", which costs the Messenger its
 * verification and nothing else.
 */
export async function fetchIntercomUserJwt(
  fetchImpl: typeof fetch = fetch,
): Promise<IntercomUserJwt | null> {
  try {
    const response = await fetchImpl(INTERCOM_JWT_ENDPOINT, {
      credentials: 'same-origin',
      cache: 'no-store',
      // Signed out, the route redirects to the sign-in page. That is not a
      // token, so do not follow it.
      redirect: 'error',
      signal: AbortSignal.timeout(INTERCOM_JWT_FETCH_TIMEOUT_MS),
    });

    if (!response.ok) {
      return null;
    }

    const parsed = IntercomUserJwtSchema.safeParse(await response.json());

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

const intercomJwtCache = createIntercomJwtCache(fetchIntercomUserJwt);

/** Register the Intercom JWT as the source of identify options. */
export function installIntercomIdentity(analytics: AnalyticsManager) {
  analytics.setIdentifyOptionsResolver(
    createIntercomIdentifyOptionsResolver(intercomJwtCache),
  );
}

/**
 * Sign-out: drop the cached token and end the Messenger session. Segment's
 * own `reset()` is called separately and does not reach Intercom.
 */
export function teardownIntercomIdentity() {
  intercomJwtCache.clear();
  shutdownIntercom();
}
